import type { Item, Location } from "../api";

const DB_VERSION = 4;

let dbName = "pantry-pal";

/** Point the local store at a household's database. Resets any open handle. */
export function useHousehold(householdId: number): void {
    const name = `pantry-pal-h${householdId}`;
    if (name === dbName) return;
    dbName = name;
    if (db) {
        db.close();
        db = null;
    }
}

export interface GroceryItem {
    id: number;
    name: string;
    item_id: number | null;
    min_quantity: number | null;
    mode: "manual" | "auto";
    stocked: boolean;
    notes: string | null;
    version: number;
    field_timestamps: Record<string, string> | null;
    created_at: string;
    active?: boolean;
    item_quantity?: number | null;
    item_name?: string | null;
}

export interface LocalItem extends Item {
    field_timestamps: Record<string, string> | null;
}
export interface LocalLocation extends Location {
    field_timestamps: Record<string, string> | null;
}

export interface SyncState {
    key: string;
    value: string;
}

export interface PendingMutation {
    id: string;
    type:
        | "create_item"
        | "update_item"
        | "delete_item"
        | "decrement_item"
        | "create_location"
        | "update_location"
        | "delete_location"
        | "create_grocery"
        | "update_grocery"
        | "delete_grocery";
    recordId: number | string;
    table: "items" | "locations" | "grocery_items";
    fields: Record<string, unknown>;
    clientTimestamp: string;
    status: "pending" | "in-flight" | "failed" | "resolved";
    createdAt: number;
    attempts: number;
    error?: string;
}

export interface PendingImage {
    id: string;
    mutationId: string;
    file: Blob;
    kind: "item" | "location";
}

let db: IDBDatabase | null = null;

function open(): Promise<IDBDatabase> {
    if (db) return Promise.resolve(db);
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(dbName, DB_VERSION);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
            db = request.result;
            resolve(db);
        };
        request.onupgradeneeded = (event) => {
            const database = (event.target as IDBOpenDBRequest).result;
            const oldVersion = event.oldVersion;

            // One-time reset: drop everything and recreate stores when the schema version changes.
            if (oldVersion > 0) {
                for (const name of Array.from(database.objectStoreNames)) {
                    database.deleteObjectStore(name);
                }
            }

            const itemsStore = database.createObjectStore("items", { keyPath: "id" });
            itemsStore.createIndex("location_id", "location_id", { unique: false });
            itemsStore.createIndex("category", "category", { unique: false });
            database.createObjectStore("locations", { keyPath: "id" });
            database.createObjectStore("grocery_items", { keyPath: "id" });
            const mStore = database.createObjectStore("mutations", { keyPath: "id" });
            mStore.createIndex("status", "status", { unique: false });
            mStore.createIndex("table_record", ["table", "recordId"], { unique: false });
            database.createObjectStore("images", { keyPath: "id" });
            database.createObjectStore("image_cache", { keyPath: "path" });
            database.createObjectStore("sync_state", { keyPath: "key" });
            database.createObjectStore("ai_cache", { keyPath: "cache_key" });
            database.createObjectStore("barcode_cache", { keyPath: "upc" });
        };
    });
}

function transaction(storeName: string, mode: IDBTransactionMode): Promise<IDBObjectStore> {
    return open().then((database) => {
        const tx = database.transaction(storeName, mode);
        return tx.objectStore(storeName);
    });
}

function request<T>(req: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        req.onsuccess = () => {
            const tx = req.transaction;
            if (tx && tx.mode === "readwrite") {
                tx.addEventListener("complete", () => resolve(req.result));
            } else {
                resolve(req.result);
            }
        };
        req.onerror = () => reject(req.error);
    });
}

export async function getItem(id: number): Promise<LocalItem | undefined> {
    const store = await transaction("items", "readonly");
    return (await request(store.get(id))) as LocalItem | undefined;
}

export async function listItems(): Promise<LocalItem[]> {
    const store = await transaction("items", "readonly");
    return (await request(store.getAll())) as LocalItem[];
}

export async function putItem(item: LocalItem): Promise<void> {
    const store = await transaction("items", "readwrite");
    await request(store.put(item));
}

export async function deleteItemLocal(id: number): Promise<void> {
    const store = await transaction("items", "readwrite");
    await request(store.delete(id));
}

export async function getLocation(id: number): Promise<LocalLocation | undefined> {
    const store = await transaction("locations", "readonly");
    return (await request(store.get(id))) as LocalLocation | undefined;
}

export async function listLocations(): Promise<LocalLocation[]> {
    const store = await transaction("locations", "readonly");
    return (await request(store.getAll())) as LocalLocation[];
}

export async function putLocation(location: LocalLocation): Promise<void> {
    const store = await transaction("locations", "readwrite");
    await request(store.put(location));
}

export async function deleteLocationLocal(id: number): Promise<void> {
    const store = await transaction("locations", "readwrite");
    await request(store.delete(id));
}

export async function getGroceryItem(id: number): Promise<GroceryItem | undefined> {
    const store = await transaction("grocery_items", "readonly");
    return (await request(store.get(id))) as GroceryItem | undefined;
}

export async function listGroceryItems(): Promise<GroceryItem[]> {
    const store = await transaction("grocery_items", "readonly");
    return (await request(store.getAll())) as GroceryItem[];
}

export async function putGroceryItem(grocery: GroceryItem): Promise<void> {
    const store = await transaction("grocery_items", "readwrite");
    await request(store.put(grocery));
}

export async function deleteGroceryItemLocal(id: number): Promise<void> {
    const store = await transaction("grocery_items", "readwrite");
    await request(store.delete(id));
}

export async function addMutation(
    mutation: Omit<PendingMutation, "id" | "createdAt" | "attempts" | "status">,
): Promise<PendingMutation> {
    const full: PendingMutation = {
        ...mutation,
        id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`,
        createdAt: Date.now(),
        attempts: 0,
        status: "pending",
    };
    const store = await transaction("mutations", "readwrite");
    await request(store.put(full));
    return full;
}

export async function updateMutation(mutation: PendingMutation): Promise<void> {
    const store = await transaction("mutations", "readwrite");
    await request(store.put(mutation));
}

export async function deleteMutation(id: string): Promise<void> {
    const store = await transaction("mutations", "readwrite");
    await request(store.delete(id));
}

export async function listPendingMutations(): Promise<PendingMutation[]> {
    const store = await transaction("mutations", "readonly");
    const index = store.index("status");
    const all = (await request(index.getAll("pending"))) as PendingMutation[];
    all.push(...((await request(index.getAll("failed"))) as PendingMutation[]));
    return all.sort((a, b) => a.createdAt - b.createdAt);
}

export async function listInFlightMutations(): Promise<PendingMutation[]> {
    const store = await transaction("mutations", "readonly");
    const index = store.index("status");
    return (await request(index.getAll("in-flight"))) as PendingMutation[];
}

export async function listMutationsForRecord(
    table: string,
    recordId: number | string,
): Promise<PendingMutation[]> {
    const store = await transaction("mutations", "readonly");
    const index = store.index("table_record");
    const range = IDBKeyRange.bound([table, recordId], [table, recordId]);
    return (await request(index.getAll(range))) as PendingMutation[];
}

export async function setSyncState(key: string, value: string): Promise<void> {
    const store = await transaction("sync_state", "readwrite");
    await request(store.put({ key, value }));
}

export async function getSyncState(key: string): Promise<string | undefined> {
    const store = await transaction("sync_state", "readonly");
    const row = (await request(store.get(key))) as SyncState | undefined;
    return row?.value;
}

export async function deleteSyncState(key: string): Promise<void> {
    const store = await transaction("sync_state", "readwrite");
    await request(store.delete(key));
}

export async function listSyncStateKeys(): Promise<string[]> {
    const store = await transaction("sync_state", "readonly");
    const keys: string[] = [];
    const cursor = store.openCursor();
    return new Promise((resolve, reject) => {
        cursor.onsuccess = (event) => {
            const target = event.target as IDBRequest<IDBCursorWithValue | null>;
            const c = target.result;
            if (!c) {
                resolve(keys);
                return;
            }
            keys.push(c.key as string);
            c.continue();
        };
        cursor.onerror = () => reject(cursor.error);
    });
}

export async function getMutation(id: string): Promise<PendingMutation | undefined> {
    const store = await transaction("mutations", "readonly");
    return (await request(store.get(id))) as PendingMutation | undefined;
}

export async function setAiCache(cacheKey: string, content: string): Promise<void> {
    const store = await transaction("ai_cache", "readwrite");
    await request(store.put({ cache_key: cacheKey, content, cached_at: Date.now() }));
}

export async function getAiCache(cacheKey: string): Promise<string | undefined> {
    const store = await transaction("ai_cache", "readonly");
    const row = (await request(store.get(cacheKey))) as { content: string } | undefined;
    return row?.content;
}

export async function setBarcodeCache(upc: string, data: object): Promise<void> {
    const store = await transaction("barcode_cache", "readwrite");
    await request(store.put({ upc, data, cached_at: Date.now() }));
}

export async function getBarcodeCache(upc: string): Promise<object | undefined> {
    const store = await transaction("barcode_cache", "readonly");
    const row = (await request(store.get(upc))) as { data: object } | undefined;
    return row?.data;
}

export async function storePendingImage(
    id: string,
    mutationId: string,
    file: Blob,
    kind: "item" | "location",
): Promise<void> {
    const store = await transaction("images", "readwrite");
    await request(store.put({ id, mutationId, file, kind, createdAt: Date.now() }));
}

export async function getPendingImage(
    id: string,
): Promise<{ file: Blob; kind: "item" | "location" } | undefined> {
    const store = await transaction("images", "readonly");
    const row = (await request(store.get(id))) as
        | { file: Blob; kind: "item" | "location" }
        | undefined;
    return row;
}

export async function deletePendingImage(id: string): Promise<void> {
    const store = await transaction("images", "readwrite");
    await request(store.delete(id));
}

export async function listPendingImages(): Promise<
    { id: string; mutationId: string; kind: "item" | "location" }[]
> {
    const store = await transaction("images", "readonly");
    const all = (await request(store.getAll())) as {
        id: string;
        mutationId: string;
        kind: "item" | "location";
    }[];
    return all;
}

export async function getPendingImageForRecord(
    table: string,
    recordId: number | string,
): Promise<{ file: Blob; kind: "item" | "location" } | undefined> {
    const mutations = await listMutationsForRecord(table, recordId);
    const images = await listPendingImages();
    for (const m of mutations) {
        const img = images.find((i) => i.mutationId === m.id);
        if (img) {
            return getPendingImage(img.id);
        }
    }
    return undefined;
}

export async function getCachedImage(path: string): Promise<Blob | undefined> {
    const store = await transaction("image_cache", "readonly");
    const row = (await request(store.get(path))) as { path: string; blob: Blob } | undefined;
    return row?.blob;
}

export async function putCachedImage(path: string, blob: Blob): Promise<void> {
    const store = await transaction("image_cache", "readwrite");
    await request(store.put({ path, blob }));
}

export async function listCachedImagePaths(): Promise<Set<string>> {
    const store = await transaction("image_cache", "readonly");
    const all = (await request(store.getAllKeys())) as string[];
    return new Set(all);
}

export async function reset(): Promise<void> {
    if (db) {
        db.close();
        db = null;
    }
    await new Promise<void>((resolve, reject) => {
        const req = indexedDB.deleteDatabase(dbName);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
    });
}
